import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, History, ArrowUpRight, ArrowDownLeft, Calendar } from "lucide-react";
import { database } from "../../services/firebase";
import { ref, onValue, off } from "firebase/database";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { displayTimeFromDB } from "../../utils/timeUtils";

const PointsHistoryModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const { user } = useSelector((state) => state.auth);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!isOpen || !user?.uid) return;

        const historyRef = ref(database, `Users/${user.uid}/pointsHistory`);

        onValue(historyRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const historyList = Object.keys(data).map(key => ({
                    id: key,
                    ...data[key]
                })).sort((a, b) => new Date(b.date) - new Date(a.date));
                setHistory(historyList);
            } else {
                setHistory([]);
            }
            setLoading(false);
        });

        return () => off(historyRef);
    }, [isOpen, user?.uid]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isOpen]);

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[150]"
                        onClick={onClose}
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        className="fixed inset-0 z-[151] flex items-center justify-center p-4"
                        onClick={onClose}
                    >
                        <div
                            className="bg-white dark:bg-gray-900 w-full max-w-lg rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col max-h-[80vh]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center text-yellow-600 dark:text-yellow-400">
                                        <History className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t("points_history.title", "Points History")}</h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{t("points_history.subtitle", "Your rewards timeline")}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                    aria-label={t("common.close", "Close")}
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* List */}
                            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                                {loading ? (
                                    <div className="text-center py-8 text-gray-500">{t("points_history.loading", "Loading history...")}</div>
                                ) : history.length === 0 ? (
                                    <div className="text-center py-12 flex flex-col items-center">
                                        <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                                            <Calendar className="w-6 h-6 text-gray-300 dark:text-gray-600" />
                                        </div>
                                        <p className="text-gray-500 dark:text-gray-400">{t("points_history.empty", "No point history yet.")}</p>
                                    </div>
                                ) : (
                                    history.map((item) => (
                                        <div key={item.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-800">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${item.type === "credit"
                                                    ? "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400"
                                                    : "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400"
                                                    }`}>
                                                    {item.type === "credit" ? <ArrowDownLeft className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900 dark:text-white">{item.reason || t("points_history.default_reason", "Points Adjustment")}</p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                                        {displayTimeFromDB(item.date, user?.country, user?.language, user?.timezone)}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className={`text-base font-bold notranslate ${item.type === "credit"
                                                ? "text-green-600 dark:text-green-400"
                                                : "text-gray-900 dark:text-white"
                                                }`} translate="no">
                                                {item.type === "credit" ? "+" : "-"}{item.amount}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default PointsHistoryModal;
